/**
 * BuildIntelLogo — primary brand mark: an orange hard hat on a dark rounded
 * tile. Used in the top header, on auth screens, and anywhere a compact
 * app icon is needed. Kept intentionally free of any wordmark so the
 * "BUILDINTEL AI" text can be composed alongside it.
 *
 * The colours are locked to the design tokens — dark background (#1F1F1F)
 * with the construction-orange hat (#FF9500) — so the mark reads the same
 * across dark surfaces, PDFs, and the PWA home-screen icon.
 */
export default function BuildIntelLogo({
  size = 44,
  className = "",
  animated = true,
  testId = "brand-logo",
}) {
  const gid = `bi-hat-glow-${size}`;
  const rid = `bi-hat-ring-${size}`;
  return (
    <svg
      data-testid={testId}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`shrink-0 ${animated ? "bi-logo-animated" : ""} ${className}`}
      role="img"
      aria-label="BuildIntel AI logo"
    >
      <defs>
        <radialGradient id={gid} cx="50%" cy="42%" r="60%">
          <stop offset="0%" stopColor="#FF9500" stopOpacity="0.28" />
          <stop offset="60%" stopColor="#FF9500" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#FF9500" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Dark rounded tile — brand background */}
      <rect x="1" y="1" width="62" height="62" rx="15" ry="15" fill="#1F1F1F" />
      {/* Subtle orange glow so the mark feels energised on dark surfaces */}
      <rect x="1" y="1" width="62" height="62" rx="15" ry="15" fill={`url(#${gid})`} />

      {/* Orange hard hat — the primary mark */}
      <g fill="#FF9500">
        {/* Dome */}
        <path d="M15 38 C15 24, 22 18, 32 18 C42 18, 49 24, 49 38 Z" />
        {/* Brim */}
        <rect x="11" y="37.5" width="42" height="5.2" rx="2.6" />
        {/* Central raised ridge (subtle dark cutout) */}
        <rect x="30.6" y="19.5" width="2.8" height="18" rx="1.4" fill="#1F1F1F" opacity="0.32" />
        {/* Side vents (subtle dark accents) */}
        <rect x="19"  y="30" width="7" height="1.8" rx="0.9" fill="#1F1F1F" opacity="0.32" />
        <rect x="38"  y="30" width="7" height="1.8" rx="0.9" fill="#1F1F1F" opacity="0.32" />
      </g>

      {/* Tactile inner hairline ring */}
      <rect
        id={rid}
        x="1.5"
        y="1.5"
        width="61"
        height="61"
        rx="14.5"
        fill="none"
        stroke="rgba(255,149,0,0.22)"
        strokeWidth="1"
      />
    </svg>
  );
}
